import apiService from '../api/apiService';
import { slugify } from '../utils/slugify';
import { triggerRecipeImageGeneration } from './recipeImageGeneration';

type RecipeCreationStep = 'upload' | 'create';

export class RecipeCreationWorkflowError extends Error {
  public readonly step: RecipeCreationStep;

  constructor(step: RecipeCreationStep, message: string) {
    super(message);
    this.name = 'RecipeCreationWorkflowError';
    this.step = step;
  }
}

interface CreateRecipeWithOptionalImageParams {
  title: string;
  imageFile: File | null;
  ingredients: string;
  steps: string;
  authorId: number;
  selectedTagIds: number[];
  token: string;
}

const createSlugifiedImageFile = (imageFile: File) => {
  const originalFileNameParts = imageFile.name.split('.');
  const hasExtension = originalFileNameParts.length > 1;
  const extension = hasExtension ? originalFileNameParts.pop() : undefined;
  const baseName = hasExtension ? originalFileNameParts.join('.') : imageFile.name;
  const slugifiedBaseName = slugify(baseName);
  const slugifiedFileName = extension ? `${slugifiedBaseName}.${extension}` : slugifiedBaseName;

  return new File([imageFile], slugifiedFileName, { type: imageFile.type });
};

const uploadRecipeImage = async (imageFile: File) => {
  try {
    return await apiService.uploadImage(createSlugifiedImageFile(imageFile));
  } catch (error: unknown) {
    throw new RecipeCreationWorkflowError(
      'upload',
      error instanceof Error ? error.message : 'Erreur inconnue'
    );
  }
};

export const createRecipeWithOptionalImage = async ({
  title,
  imageFile,
  ingredients,
  steps,
  authorId,
  selectedTagIds,
  token,
}: CreateRecipeWithOptionalImageParams) => {
  const imageUrl = imageFile ? await uploadRecipeImage(imageFile) : null;

  try {
    const createdRecipe = await apiService.addRecipe(
      {
        title,
        image: imageUrl,
        ingredients,
        steps,
        author_id: authorId,
      },
      selectedTagIds
    );

    if (!imageFile) {
      triggerRecipeImageGeneration(createdRecipe.id, token);
    }

    return {
      createdRecipe,
      imageUrl,
    };
  } catch (error: unknown) {
    throw new RecipeCreationWorkflowError(
      'create',
      error instanceof Error ? error.message : 'Erreur inconnue'
    );
  }
};
